import DialogTitle from '@mui/material/DialogTitle'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  TextField,
} from '@mui/material'
import { FC, forwardRef, ReactElement, Ref, useContext, useState } from 'react'
import { TransitionProps } from '@mui/material/transitions'
import Slide from '@mui/material/Slide'
import { SnackbarHelperContext } from '../../../../../contexts/SnackbarHelperContext'
import CloseIcon from '@mui/icons-material/Close'
import LinkIcon from '@mui/icons-material/Link'
import { arrayUnion, doc, getDoc } from 'firebase/firestore'
import { firebase_firestore } from '../../../../../common/firebaseConfig'
import { updateDoc } from 'firebase/firestore'
import { useApplicationSuiteContext } from '../../../../../contexts/ApplicationSuiteProvider'
import { getMemberPortalUrl } from '../../../../../common/utils/memberPortalUrl'

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement<any, any>
  },
  ref: Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

interface GeneralDialogProps {
  open: boolean
  title: string
  onClose: () => void
  shareable: any
}

const ShareDialog: FC<GeneralDialogProps> = ({ open, title, onClose, shareable }) => {
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)
  const { tradingHouse } = useApplicationSuiteContext()
  const [emails, setEmails] = useState<string[]>([])
  const [isInviting, setIsInviting] = useState(false)

  function copyLinkToClipboard() {
    const link = getMemberPortalUrl(
      tradingHouse,
      `/dashboard/strategy-suite?shareable=${shareable.linkId}`
    )
    try {
      if (link) {
        navigator.clipboard.writeText(link)
        setSnackbarSettings({
          message: 'Link copied to clipboard.',
          severity: 'success',
          autoHideDuration: 6000,
        })
      }
    } catch (err) {
      console.error('Failed to copy: ', err)
    }
  }

  function isValidEmailAddress(email: string) {
    // Basic email validation that checks for:
    // - At least one character before @
    // - Valid domain after @ (no spaces)
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  async function inviteEmailsToShareable() {
    if (!shareable.linkId) {
      setSnackbarSettings({
        message: 'Shareable link not found.',
        severity: 'error',
        autoHideDuration: 6000,
      })
      return
    }

    setIsInviting(true)

    try {
      const shareableRef = doc(firebase_firestore, `shareables/${shareable.linkId}`)
      const shareableDoc = await getDoc(shareableRef)
      if (shareableDoc.exists()) {
        await updateDoc(shareableRef, {
          emailsInvited: arrayUnion(
            ...emails.filter((email: string) => isValidEmailAddress(email))
          ),
        })
      }
      setSnackbarSettings({
        message: 'Emails invited to shareable.',
        severity: 'success',
        autoHideDuration: 6000,
      })
      setEmails([])
    } catch (error) {
      console.error('Error inviting emails to shareable:', error)
    } finally {
      setIsInviting(false)
    }
  }

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {title}
        <Box display={'flex'} gap={1} alignItems={'center'}>
          {/* rotate link 45deg */}
          <Button
            onClick={copyLinkToClipboard}
            startIcon={<LinkIcon sx={{ transform: 'rotate(305deg)' }} />}
          >
            Copy Link
          </Button>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box display={'flex'} gap={1} alignItems={'center'}>
          <TextField
            placeholder="Emails, comma separated"
            fullWidth
            size="small"
            value={emails.join(',')}
            onChange={(e) =>
              setEmails(e.target.value.split(',').map((email: string) => email.trim()))
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isInviting) {
                e.preventDefault()
                inviteEmailsToShareable()
              }
            }}
          />
          <Button
            variant="contained"
            disabled={isInviting}
            onClick={(e) => {
              e.preventDefault()
              inviteEmailsToShareable()
            }}
          >
            {isInviting ? <CircularProgress size={20} /> : 'Share'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default ShareDialog
